<template>
  <section class="faq-list-parent">
    <div v-if="search" class="search">
      <input v-model="keyword" type="search" placeholder="Search for questions and answers" />
    </div>

    <div v-if="filteredFaqs.length > 0" ref="faqListEl" class="faq-list">
      <div class="faq-category" v-for="(faqs, category) in filteredAndGroupedFaqs">
        <div class="title">{{ category }}</div>
        <div class="faqs">
          <FaqItem
            class="faq"
            v-for="faq in faqs"
            :id="`faq-question:${faq.id}`"
            :question="faq.question"
            :answer="faq.answer"
            :expand="faq.expand"
            @toggle="faq.expand = !faq.expand"
          />
        </div>
      </div>
    </div>

    <div class="faq-footer" v-if="page">
      <div>Have more questions?</div>
      <div>
        <NavLink href="/how-tourhero-works">
          <Button id="faq:see-all" color="green" invert>See all FAQs</Button>
        </NavLink>
      </div>
    </div>

    <div class="faq-footer" v-else>
      <div>💬 Still have questions?</div>
      <div>
        We understand that starting something new can feel uncertain. Our team is here to make it easy, transparent, and
        rewarding for you. <a href="mailto:hello@tourhero.com">Email us</a> with your questions get personal guidance
        from one of our TourHero experts.
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { groupBy } from 'lodash'
import { Ref, ref, computed, watch } from 'vue'
import data from 'lib/data/faqs.yml'
import FaqItem from '@/modules/SupportHub/FaqItem.vue'
import useTextHighlight from '@/composables/useTextHighlight'
import Button from '@/components/Theme2/Button.vue'
import NavLink from '@/components/NavLink.vue'

type Faq = { category: string; question: string; answer: string; pages?: string }
type FaqUI = Faq & { id: number; expand: boolean }

const props = defineProps<{ page?: string; search?: boolean }>()
const allFaqs: Ref<FaqUI[]> = ref((data as Faq[]).map((faq, i) => ({ ...faq, id: i + 1, expand: i === 0 })))
const filteredFaqs = computed(() =>
  allFaqs.value
    .filter(faq => !props.page || faq.pages?.includes(props.page))
    .filter(faq => {
      const q = keyword.value.toLowerCase()
      return [faq.category, faq.question, faq.answer].some(s => s.toLowerCase().includes(q))
    }),
)
const filteredAndGroupedFaqs = computed(() => groupBy(filteredFaqs.value, 'category'))

const keyword = ref('')
const faqListEl = ref<HTMLElement>()

useTextHighlight(faqListEl, keyword)
watch(keyword, expandQuestionsWhereAnswerContainsQuery)

function expandQuestionsWhereAnswerContainsQuery(q: string) {
  const faqs = filteredFaqs.value
  faqs.forEach((faq, i) => {
    faq.expand = i === 0 || (q.length > 0 && faq.answer.toLowerCase().includes(q.toLowerCase()))
  })
}
</script>

<style lang="scss" scoped>
.faq-list-parent {
  @include base-font;
  @include flex-column(16px);

  .search {
    margin-bottom: 16px;

    @include desktop {
      padding-inline: 60px;
    }

    input {
      width: 100%;
      border-radius: 15px;
      border: 1px solid $grey-50;
      background: #fff;
      padding: 12px 20px;
      outline: none;

      &:focus {
        border-color: $brand-tertiary;
      }
    }
  }

  .faq-list {
    @include flex-column(16px);

    .faqs {
      .faq:not(:first-child) {
        padding-top: 16px;
      }
      .faq:not(:last-child) {
        padding-bottom: 16px;
        border-bottom: 1px solid $grey-50;
      }
    }

    ::highlight(search) {
      background-color: rgba(241, 196, 15, 0.4);
    }
  }

  .faq-list .faq-category,
  .faq-footer {
    display: grid;
    grid-template-columns: 312px 1fr;
    border-radius: 15px;
    border: 1px solid rgba(255, 255, 255, 0.5);
    background: var(--background, $grey-25);
    padding: 32px;
    gap: 20px;

    @include mobile {
      grid-template-columns: 1fr;
    }
  }

  .faq-list .faq-category .title,
  .faq-footer > div:first-child {
    text-align: left;
    font-size: 24px;
    font-weight: 600;
    line-height: 28px;
  }

  .faq-footer a {
    color: $brand-tertiary;
  }
}
</style>
